import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  createJourneyBotAiChatCompletion,
  submitJourneyBotActionPlan,
} from './api/journeyBotApi';
import { useData } from './DataProviderContext';
import { countGoalFocusWords, GOAL_FOCUS_MAX_WORDS } from './storage/actionItemModel';

const INITIAL_ASSISTANT_MESSAGE = {
  id: 'welcome',
  role: 'assistant',
  content: 'Let’s turn what you want to achieve into a journey. What goal would you like to work toward?',
};

const JOURNEY_CHAT_SYSTEM_PROMPT = `You are JourneyBot's guided journey builder inside the app.

Your single purpose is to help the user create one useful goal journey. Remember this throughout the conversation, even when the discussion explores alternatives. Be supportive, journey-type neutral, and concise. Ask at most one focused question per response. Gradually learn the user's goal, current situation, desired outcome, constraints, decisions, blockers, and practical next actions. Do not claim the journey has been saved.

During normal conversation, respond in plain language and keep steering toward a journey that can be represented by the JourneyBot schema. The goal title must be seven words or fewer. Do not output JSON until a later instruction explicitly asks you to create the draft.`;

const JOURNEY_DRAFT_INSTRUCTION = `Create the JourneyBot journey draft now from our conversation.

Return only one valid JSON object without Markdown or commentary. Use exactly these top-level fields: goal, startState, context, endState, assumptions, constraints, decisions, action_items, blockers, open_questions, next_action, source_summary, confidence, created_at.

Rules:
- goal is a clear title of seven words or fewer.
- context is 300 characters or fewer.
- assumptions, constraints, decisions, blockers, and open_questions are arrays of strings.
- action_items contains one to six concise, practical actions in a useful order.
- each action item has exactly: id, title, description, status, relevance, user_note.
- each id uses only letters, numbers, underscores, or hyphens and is unique.
- status is active, deferred, or completed.
- relevance is relevant, irrelevant, or unknown.
- user_note is an empty string unless the user explicitly supplied a note.
- confidence is a number from 0 to 1.
- created_at is an ISO 8601 date-time with a timezone offset.
- use empty strings or arrays when information was not established; do not invent personal facts.`;

const stringList = (value) => (
  Array.isArray(value) ? value.filter((item) => typeof item === 'string').map((item) => item.trim()).filter(Boolean) : []
);

const safeString = (value) => (typeof value === 'string' ? value.trim() : '');

const actionId = (value, index, usedIds) => {
  const base = safeString(value)
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100) || `action-${index + 1}`;
  let candidate = base;
  let suffix = 2;
  while (usedIds.has(candidate)) {
    candidate = `${base.slice(0, 94)}-${suffix}`;
    suffix += 1;
  }
  usedIds.add(candidate);
  return candidate;
};

export const parseJourneyDraft = (content) => {
  const text = String(content || '').trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');
  if (firstBrace < 0 || lastBrace <= firstBrace) {
    throw new Error('The AI did not return a journey draft. Continue the conversation and try again.');
  }

  let parsed;
  try {
    parsed = JSON.parse(text.slice(firstBrace, lastBrace + 1));
  } catch {
    throw new Error('The AI returned an incomplete journey draft. Try creating the draft again.');
  }

  const candidate = parsed?.structured || parsed;
  const goal = safeString(candidate?.goal);
  if (!goal) throw new Error('The journey draft needs a goal. Tell the AI what you want to achieve and try again.');
  if (countGoalFocusWords(goal) > GOAL_FOCUS_MAX_WORDS) {
    throw new Error(`The AI goal must be ${GOAL_FOCUS_MAX_WORDS} words or fewer. Ask it to shorten the goal and try again.`);
  }

  if (!Array.isArray(candidate?.action_items) || candidate.action_items.length === 0) {
    throw new Error('The journey draft needs at least one action. Discuss a practical next step and try again.');
  }

  const usedIds = new Set();
  const actions = candidate.action_items.map((item, index) => {
    const title = safeString(item?.title);
    if (!title) throw new Error(`Action ${index + 1} needs a title. Try creating the draft again.`);
    return {
      id: actionId(item?.id, index, usedIds),
      title,
      description: safeString(item?.description),
      status: ['active', 'deferred', 'completed'].includes(item?.status) ? item.status : 'active',
      relevance: ['relevant', 'irrelevant', 'unknown'].includes(item?.relevance) ? item.relevance : 'unknown',
      user_note: safeString(item?.user_note),
    };
  });

  const confidence = Number(candidate?.confidence);
  return {
    goal,
    startState: safeString(candidate?.startState),
    context: safeString(candidate?.context).slice(0, 300),
    endState: safeString(candidate?.endState),
    assumptions: stringList(candidate?.assumptions),
    constraints: stringList(candidate?.constraints),
    decisions: stringList(candidate?.decisions),
    action_items: actions,
    blockers: stringList(candidate?.blockers),
    open_questions: stringList(candidate?.open_questions),
    next_action: safeString(candidate?.next_action) || actions[0].title,
    source_summary: safeString(candidate?.source_summary),
    confidence: Number.isFinite(confidence) && confidence >= 0 && confidence <= 1 ? confidence : 0.6,
    created_at: new Date().toISOString(),
  };
};

const apiConversation = (messages, finalInstruction) => [
  { role: 'system', content: JOURNEY_CHAT_SYSTEM_PROMPT },
  ...messages.filter((message) => message.id !== 'welcome').slice(-17).map(({ role, content }) => ({ role, content })),
  ...(finalInstruction ? [{ role: 'user', content: finalInstruction }] : []),
];

const makeRecordId = (goal) => {
  const slug = goal.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 70) || 'journey';
  const suffix = window.crypto?.randomUUID?.().slice(0, 8) || Date.now().toString(36);
  return `ai-${slug}-${suffix}`;
};

export default function JourneyAiChatDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectGoalFocus } = useData();
  const [messages, setMessages] = useState([INITIAL_ASSISTANT_MESSAGE]);
  const [input, setInput] = useState('');
  const [draft, setDraft] = useState(null);
  const [status, setStatus] = useState({ type: 'idle', message: '' });
  const [isSending, setIsSending] = useState(false);
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setMessages([INITIAL_ASSISTANT_MESSAGE]);
    setInput('');
    setDraft(null);
    setStatus({ type: 'idle', message: '' });
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages, draft]);

  const hasUserMessage = useMemo(() => messages.some((message) => message.role === 'user'), [messages]);
  const journeyAdded = status.type === 'success';

  const sendMessage = async (event) => {
    event.preventDefault();
    const content = input.trim();
    if (!content || isSending) return;

    const userMessage = { id: `user-${Date.now()}`, role: 'user', content };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setDraft(null);
    setStatus({ type: 'idle', message: '' });
    setIsSending(true);

    try {
      const result = await createJourneyBotAiChatCompletion(apiConversation(nextMessages));
      setMessages((current) => [...current, {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: result.assistant.content,
      }]);
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'The AI chat is unavailable right now.' });
    } finally {
      setIsSending(false);
    }
  };

  const createDraft = async () => {
    setStatus({ type: 'idle', message: '' });
    setIsCreatingDraft(true);
    try {
      const result = await createJourneyBotAiChatCompletion(apiConversation(messages, JOURNEY_DRAFT_INSTRUCTION));
      const nextDraft = parseJourneyDraft(result.assistant.content);
      setDraft(nextDraft);
      setMessages((current) => [...current, {
        id: `draft-${Date.now()}`,
        role: 'assistant',
        content: 'I’ve prepared a journey draft. Review it below, or keep chatting if you want to change it.',
      }]);
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'The journey draft could not be created.' });
    } finally {
      setIsCreatingDraft(false);
    }
  };

  const addToBoard = async () => {
    if (!draft || isAdding) return;
    setIsAdding(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const result = await submitJourneyBotActionPlan(makeRecordId(draft.goal), draft);
      const openedJourney = await selectGoalFocus({
        goal: result.plan.goal,
        journeyId: `journeybot-${result.journeyId}`,
        apiJourneyId: result.journeyId,
        source: 'journeybot-api',
      });
      if (!openedJourney) {
        throw new Error('The journey was saved, but could not be opened on the board. Refresh and open it from Goal focus.');
      }
      setStatus({ type: 'success', message: 'Journey added to your board.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'The journey could not be added.' });
    } finally {
      setIsAdding(false);
    }
  };

  const closeDialog = () => {
    if (!isSending && !isCreatingDraft && !isAdding) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      aria-labelledby="journey-ai-chat-title"
    >
      <DialogTitle id="journey-ai-chat-title" sx={{ pr: 7 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AutoAwesomeOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 800 }}>Create a journey with AI</Typography>
            <Typography variant="body2" color="text.secondary">Chat toward a structured goal journey</Typography>
          </Box>
        </Stack>
        <IconButton aria-label="Close AI journey chat" onClick={closeDialog} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Alert severity="info">
            This conversation is guided toward a goal, journey states, and practical actions. Nothing is added until you review the draft.
          </Alert>

          <Box
            aria-live="polite"
            sx={{ minHeight: 240, maxHeight: { xs: 'none', sm: 380 }, overflowY: 'auto', px: 0.5 }}
          >
            <Stack spacing={1.5}>
              {messages.map((message) => (
                <Paper
                  key={message.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    maxWidth: '88%',
                    alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                    bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                    color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  <Typography variant="body2">{message.content}</Typography>
                </Paper>
              ))}
              {isSending && <CircularProgress size={22} aria-label="AI is responding" />}
              <div ref={endRef} />
            </Stack>
          </Box>

          <Box component="form" onSubmit={sendMessage}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
              <TextField
                label="Message JourneyBot"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                multiline
                maxRows={5}
                fullWidth
                disabled={isSending || isCreatingDraft || isAdding || journeyAdded}
                inputProps={{ maxLength: 2000 }}
              />
              <Button
                type="submit"
                variant="contained"
                endIcon={<SendOutlinedIcon />}
                disabled={!input.trim() || isSending || isCreatingDraft || isAdding || journeyAdded}
                sx={{ minHeight: 56, width: { xs: '100%', sm: 'auto' } }}
              >
                Send
              </Button>
            </Stack>
          </Box>

          {status.type !== 'idle' && (
            <Alert severity={status.type === 'success' ? 'success' : 'error'}>{status.message}</Alert>
          )}

          {draft && (
            <Paper variant="outlined" sx={{ p: 2 }} data-testid="journey-ai-draft">
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>Journey draft</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>{draft.goal}</Typography>
                  <Typography variant="body2" color="text.secondary">{draft.source_summary || draft.context}</Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`${draft.action_items.length} actions`} size="small" />
                  {draft.blockers.length > 0 && <Chip label={`${draft.blockers.length} blockers`} size="small" variant="outlined" />}
                </Stack>
                <Box component="ol" sx={{ pl: 3, my: 0 }}>
                  {draft.action_items.map((item) => (
                    <Typography component="li" variant="body2" key={item.id} sx={{ mb: 0.5 }}>
                      {item.title}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
        <Button onClick={closeDialog} disabled={isSending || isCreatingDraft || isAdding} sx={{ width: { xs: '100%', sm: 'auto' } }}>
          {status.type === 'success' ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant={draft ? 'outlined' : 'contained'}
          onClick={createDraft}
          disabled={!hasUserMessage || isSending || isCreatingDraft || isAdding || journeyAdded}
          startIcon={isCreatingDraft ? <CircularProgress size={18} /> : <AutoAwesomeOutlinedIcon />}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {draft ? 'Recreate draft' : 'Create journey draft'}
        </Button>
        {draft && (
          <Button
            variant="contained"
            onClick={addToBoard}
            disabled={isAdding || status.type === 'success'}
            startIcon={isAdding ? <CircularProgress size={18} /> : null}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Add to journey board
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
